import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $composeConvertFromMarkdownString,
  $composeConvertToMarkdownString,
} from "@mirage/mosaics/ComposeAssistant/components/editor/ComposeMarkdownTransformers";
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  REDO_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { memo, useCallback, useEffect, useRef } from "react";

export interface MessageHistoryPluginProps {
  supportsTables: boolean;
  isStreaming: boolean;
  markdownContent: string;
  onChangeContent: (content: string) => void;
}

/**
 * This plugin is responsible for maintaining an internal undo/redo
 * history for the Lexical editor for streaming responses
 *
 * 1. Streaming mode (`isStreaming === true`) – The editor is being updated by
 *    an AI stream. When streaming begins we snapshot the current markdown so
 *    the entire streamed block can be reverted with a single undo action.
 * 2. User-edit mode (`isStreaming === false`) – Regular user keystrokes and
 *    programmatic transformations. Every meaningful markdown change is pushed
 *    onto a stack, pruning any stale redo states along the way.
 */
export const MessageHistoryPlugin = memo(
  ({
    supportsTables,
    isStreaming,
    markdownContent,
    onChangeContent,
  }: MessageHistoryPluginProps) => {
    const [editor] = useLexicalComposerContext();
    const history = useRef<{ stack: string[]; index: number }>({
      stack: [],
      index: -1,
    });
    const wasStreaming = useRef(false);
    const suppressPushRef = useRef(false);
    const isInitialized = useRef(false);

    useEffect(() => {
      if (isStreaming && !wasStreaming.current) {
        const currentContent = editor
          .getEditorState()
          .read(() =>
            $composeConvertToMarkdownString({ includeSpecialAttributes: true }),
          );

        // When we enter streaming mode, capture the current content so the
        // whole streamed update can be rolled back with one undo step. Avoid
        // pushing a duplicate entry if the top of the stack already matches.
        const lastContent = history.current.stack[history.current.index];
        if (currentContent !== lastContent) {
          history.current.stack.splice(history.current.index + 1);
          history.current.stack.push(currentContent);
          history.current.index = history.current.stack.length - 1;
        }
      }
      wasStreaming.current = isStreaming;
    }, [isStreaming, editor]);

    useEffect(() => {
      if (suppressPushRef.current) {
        // The current editor change was triggered programmatically via
        // `updateEditorState` (undo/redo). We intentionally skip recording this
        // change in the history stack.
        suppressPushRef.current = false; // reset the flag for future edits
        return;
      }
      if (!isStreaming) {
        const current = history.current;

        // Initialize with the first meaningful content
        if (
          !isInitialized.current &&
          markdownContent &&
          markdownContent.trim()
        ) {
          current.stack = [markdownContent];
          current.index = 0;
          isInitialized.current = true;
          return;
        }

        // Only record changes after initialization
        if (isInitialized.current) {
          const latestRecorded = current.stack[current.index];
          if (markdownContent !== latestRecorded && markdownContent.trim()) {
            // If the user types after undoing, discard any obsolete redo
            // entries before pushing the fresh state onto the stack.
            current.stack.splice(current.index + 1);
            current.stack.push(markdownContent);
            current.index = current.stack.length - 1;

            // After adding a new history entry, notify the toolbar so it can
            // enable or disable the undo/redo buttons accordingly.
            setTimeout(() => {
              const canUndo = current.index > 0 && current.stack.length > 1;
              const canRedo = current.index < current.stack.length - 1;
              editor.dispatchCommand(CAN_UNDO_COMMAND, canUndo);
              editor.dispatchCommand(CAN_REDO_COMMAND, canRedo);
            }, 50);
          }
        }
      }
    }, [markdownContent, isStreaming, editor]);

    const updateEditorState = useCallback(
      (content: string) => {
        suppressPushRef.current = true;
        editor.update(
          () => {
            $composeConvertFromMarkdownString(content, supportsTables);
          },
          {
            onUpdate: () => {
              suppressPushRef.current = false;
              onChangeContent(content);
            },
          },
        );
      },
      [editor, supportsTables, onChangeContent],
    );

    const handleUndo = useCallback(() => {
      if (
        !isStreaming &&
        history.current.index > 0 &&
        history.current.stack.length > 1
      ) {
        const newIndex = history.current.index - 1;
        history.current.index = newIndex;
        updateEditorState(history.current.stack[newIndex]);

        // After performing undo, recalculate the availability of undo/redo and
        // notify the toolbar so its buttons stay in sync.
        setTimeout(() => {
          const canUndo =
            history.current.index > 0 && history.current.stack.length > 1;
          const canRedo =
            history.current.index < history.current.stack.length - 1;
          editor.dispatchCommand(CAN_UNDO_COMMAND, canUndo);
          editor.dispatchCommand(CAN_REDO_COMMAND, canRedo);
        }, 0);
      }
      return true;
    }, [isStreaming, updateEditorState, editor]);

    const handleRedo = useCallback(() => {
      if (
        !isStreaming &&
        history.current.index < history.current.stack.length - 1
      ) {
        const newIndex = history.current.index + 1;
        history.current.index = newIndex;
        updateEditorState(history.current.stack[newIndex]);

        // After performing redo, recalculate the availability of undo/redo and
        // notify the toolbar so its buttons stay in sync.
        setTimeout(() => {
          const canUndo = history.current.index > 0;
          const canRedo =
            history.current.index < history.current.stack.length - 1;
          editor.dispatchCommand(CAN_UNDO_COMMAND, canUndo);
          editor.dispatchCommand(CAN_REDO_COMMAND, canRedo);
        }, 0);
      }
      return true;
    }, [isStreaming, updateEditorState, editor]);

    useEffect(() => {
      return editor.registerCommand(
        UNDO_COMMAND,
        handleUndo,
        COMMAND_PRIORITY_CRITICAL,
      );
    }, [editor, handleUndo]);

    useEffect(() => {
      return editor.registerCommand(
        REDO_COMMAND,
        handleRedo,
        COMMAND_PRIORITY_CRITICAL,
      );
    }, [editor, handleRedo]);

    // Initialize button states after a delay to ensure toolbar handlers are registered
    useEffect(() => {
      const timer = setTimeout(() => {
        // Fire an initial availability check once the toolbar has had time to
        // register its command listeners. This ensures the buttons render with
        // the correct enabled/disabled state on first paint.
        const canUndo =
          history.current.index > 0 && history.current.stack.length > 1;
        const canRedo =
          history.current.index < history.current.stack.length - 1;
        editor.dispatchCommand(CAN_UNDO_COMMAND, canUndo);
        editor.dispatchCommand(CAN_REDO_COMMAND, canRedo);
      }, 200);

      return () => clearTimeout(timer);
    }, [editor]);

    return null;
  },
);

MessageHistoryPlugin.displayName = "MessageHistoryPlugin";
